import {
  fetchSignInMethodsForEmail,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import i18next from 'i18next';
import { captureException } from '@sentry/browser';

import '@pages/app';
import { globalSpinnerController } from '@components/Spinner';

import {
  isHTMLElement,
  isInputElement,
  isImageElement,
} from '@utils/elementTypeGuards';
import {
  auth,
  idTokenHandler,
  isCalledFromParentSite,
} from '@services/authService';
import COLORS from '@constants/colors';
import {
  sharedData,
  clearCredentialForLinkAccount,
  clearSocialSignInError,
} from '@shares/dataManager';
import { ACTION_TYPES } from '@constants/actions';
import saveProviderType from '@/utils/saveProviderType';
import getIsNeedOnboarding from '@/utils/getIsNeedOnboarding';
import shouldSkipOnboarding from '@/utils/shouldSkipOnboarding';
import { isAppleDeviceWebApp } from '@/utils/uaParser';
import { postTokensToIOSApp } from '@/utils/IOSwebkitMessageHandler';
import { moveToAndroidLoginSuccessPage } from '@/services/androidAppService';
import { PAGE_PATHS } from '@/constants/url';
import { linkEmailAccount } from '@/services/linkAccountService';

const Enter = 'Enter';

const savedId = localStorage.getItem('savedId');

let formValidationErrors = {
  email: {
    isValid: true,
    message: '',
  },
  password: {
    isValid: true,
    message: '',
  },
};

const PasswordLoginContainer = document.getElementById(
  'password-login-container',
) as HTMLElement;

const emailEl = PasswordLoginContainer.querySelector('#email');
const emailLabel = PasswordLoginContainer.querySelector('#email_label');
const emailError = PasswordLoginContainer.querySelector('#email_error');
const passwordEl = PasswordLoginContainer.querySelector('#password');
const passwordLabel = PasswordLoginContainer.querySelector('#password_label');
const pwVisible = PasswordLoginContainer.querySelector('#pw_visible');
const passwordError = PasswordLoginContainer.querySelector('#password_error');
const checkBox = PasswordLoginContainer.querySelector('#check_id_area');
const saveIdChk = PasswordLoginContainer.querySelector('#save_id');
const loginBtn = PasswordLoginContainer.querySelector('#email-login-btn');
const saveIdBox = PasswordLoginContainer.querySelector('.check_id_label');
const loginExist = PasswordLoginContainer.querySelector('#login_exist');
const resetPw = PasswordLoginContainer.querySelector('.reset-pw');
const forgetPw = PasswordLoginContainer.querySelector('.forget-pw');
const blockUserDialog =
  document.querySelector<HTMLDialogElement>('.block-user-dialog');

const blockUserDialogOk = blockUserDialog?.querySelector('.ok');
const blockUserDialogTitle = blockUserDialog?.querySelector('#dialog_title');
const blockUserDialogDesc1 = blockUserDialog?.querySelector('#desc1');
const blockUserDialogDesc2 = blockUserDialog?.querySelector('#desc2');

const linkAccountErrorDialog = document.querySelector<HTMLDialogElement>(
  '.link-account-error-dialog',
);
const linkAccountErrorDialogOk = linkAccountErrorDialog?.querySelector('.ok');
const linkAccountErrorDialogTitle =
  linkAccountErrorDialog?.querySelector('#dialog_title');
const linkAccountErrorDialogDesc1 =
  linkAccountErrorDialog?.querySelector('#desc1');

const urlParams = new URLSearchParams(window.location.search);
const mode = urlParams.get('mode');
const from = urlParams.get('from');
const isLinkingSocialAccount = mode === 'link';

const doesFormHaveErrors = () => {
  return Object.values(formValidationErrors).some(
    ({ isValid }) => isValid === false,
  );
};

const applyBorderBottomErrorStyle = (element: HTMLElement) => {
  element.style.setProperty(
    'border-bottom',
    `solid ${COLORS.ERROR} 1px`,
    'important',
  );
};

const getIsEmailValidFormat = (email: string) => {
  let expText = /^[A-Za-z0-9_\.\-]+@[A-Za-z0-9\-]+\.[A-Za-z0-9\-]+/;

  return expText.test(email);
};

const focusAndAnimateError = (
  inputElement: HTMLInputElement,
  errorElement: HTMLElement,
) => {
  inputElement.focus();
  errorElement.animate(
    [
      { transform: 'translate(10px, 0)' },
      { transform: 'translate(0, 0)' },
      { transform: 'translate(10px, 0)' },
      { transform: 'translate(0, 0)' },
      { transform: 'translate(10px, 0)' },
      { transform: 'translate(0, 0)' },
    ],
    {
      duration: 500,
    },
  );
};

if (
  isHTMLElement(PasswordLoginContainer) &&
  isInputElement(emailEl) &&
  isHTMLElement(emailError) &&
  isInputElement(passwordEl) &&
  isImageElement(pwVisible) &&
  isHTMLElement(passwordError) &&
  isHTMLElement(checkBox) &&
  isInputElement(saveIdChk) &&
  isHTMLElement(loginBtn) &&
  isHTMLElement(saveIdBox) &&
  isHTMLElement(loginExist) &&
  isHTMLElement(emailLabel) &&
  isHTMLElement(passwordLabel) &&
  isHTMLElement(resetPw) &&
  isHTMLElement(forgetPw) &&
  isHTMLElement(blockUserDialog) &&
  isHTMLElement(blockUserDialogOk) &&
  isHTMLElement(blockUserDialogTitle) &&
  isHTMLElement(blockUserDialogDesc1) &&
  isHTMLElement(blockUserDialogDesc2) &&
  linkAccountErrorDialog instanceof HTMLDialogElement &&
  isHTMLElement(linkAccountErrorDialogOk) &&
  isHTMLElement(linkAccountErrorDialogTitle) &&
  isHTMLElement(linkAccountErrorDialogDesc1)
) {
  loginBtn.textContent = isLinkingSocialAccount
    ? i18next.t('login.btn.link')
    : i18next.t('login.btn.login');
  emailLabel.textContent = i18next.t('email');
  passwordLabel.textContent = i18next.t('password');
  saveIdBox.textContent = i18next.t('saveId');
  loginExist.textContent = i18next.t('loginWithExistAccount');
  forgetPw.textContent = i18next.t('login.btn.forgetPw');
  resetPw.textContent = i18next.t('login.btn.resetPw');
  blockUserDialogOk.textContent = i18next.t('login.btn.ok');
  blockUserDialogTitle.textContent = i18next.t('login.block.title');
  blockUserDialogDesc1.textContent = i18next.t('login.block.desc1');
  blockUserDialogDesc2.textContent = i18next.t('login.block.desc2');

  linkAccountErrorDialogTitle.textContent = i18next.t(
    'index.linkAccountErrorDialog.title',
  );
  linkAccountErrorDialogOk.textContent = i18next.t(
    'index.linkAccountErrorDialog.ok',
  );

  const showLinkAccountErrorDialog = (reason: 'alreadyInUse' | 'generic') => {
    linkAccountErrorDialogDesc1.textContent = i18next.t(
      `index.linkAccountErrorDialog.${reason}`,
    );
    linkAccountErrorDialog.showModal();
  };

  linkAccountErrorDialogOk.addEventListener('click', () =>
    linkAccountErrorDialog.close(),
  );

  blockUserDialogOk.addEventListener('click', () => blockUserDialog.close());

  if (savedId) {
    saveIdChk.checked = true;
    emailEl.value = JSON.parse(savedId);
  }

  const errorHandler = ({
    type,
    message,
    isValid,
  }: {
    type: 'email' | 'password';
    message: string;
    isValid: boolean;
  }) => {
    formValidationErrors[type] = { isValid, message };
    updateErrorDisplay({ type, message, isValid });
  };

  const updateErrorDisplay = ({
    type,
    message,
    isValid,
  }: {
    type: 'email' | 'password';
    message: string;
    isValid: boolean;
  }) => {
    const errorElement = type === 'email' ? emailError : passwordError;
    const inputElement = type === 'email' ? emailEl : passwordEl;

    errorElement.textContent = message;

    if (isValid) {
      inputElement.style.setProperty('border-bottom', 'none');
    } else {
      applyBorderBottomErrorStyle(inputElement);
    }
  };

  const handleFormValidation = async () => {
    try {
      await handleEmailValidation();
      handlePasswordValidation();
    } catch {}
  };

  const handleEmailValidation = async () => {
    const email = emailEl.value;

    if (email === '') {
      errorHandler({
        type: 'email',
        message: i18next.t('emailEmpty'),
        isValid: false,
      });

      return;
    }

    if (getIsEmailValidFormat(email) === false) {
      errorHandler({
        type: 'email',
        message: i18next.t('emailValidation'),
        isValid: false,
      });

      return;
    }

    const usedProviders = await fetchSignInMethodsForEmail(auth, email);

    const providerMessages: { [key: string]: string } = {
      'google.com': 'google',
      'facebook.com': 'facebook',
      'apple.com': 'apple',
      'oidc.kakao': 'kakao',
      password: 'email',
    };

    const errorMessageKey =
      providerMessages[usedProviders[0]] ?? 'userNotFound';

    const result = await fetchSignInMethodsForEmail(auth, email);
    const isEmailExist = result.includes('password');

    errorHandler({
      type: 'email',
      message: isEmailExist
        ? ''
        : i18next.t(`register.userExist.${errorMessageKey}`),
      isValid: isEmailExist ? true : false,
    });
  };

  const handlePasswordValidation = () => {
    const isPasswordEmpty = passwordEl.value === '';

    errorHandler({
      type: 'password',
      message: isPasswordEmpty ? i18next.t('passwordEmpty') : '',
      isValid: isPasswordEmpty ? false : true,
    });
  };

  const pwVisibleClickHandler = () => {
    pwVisible.src.includes('off')
      ? ((pwVisible.src = './img/visible.svg'), (passwordEl.type = 'password'))
      : ((pwVisible.src = './img/visible_off.svg'), (passwordEl.type = 'text'));

    pwVisible.classList.toggle('pw-visible-margin');
  };

  const checkBoxClickHandler = () => {
    saveIdChk.checked
      ? (saveIdChk.checked = false)
      : (saveIdChk.checked = true);
  };

  const saveIdChkClickHandler = () => {
    saveIdChk.checked
      ? (saveIdChk.checked = false)
      : (saveIdChk.checked = true);
  };
  const { callbackUrl, platform } = sharedData.queries;

  const redirectToUpdatedCallbackUrl = async ({
    idToken,
    customToken,
    uid,
    linkedMethod,
  }: {
    idToken: string;
    customToken: string;
    uid: string;
    linkedMethod?: string;
  }) => {
    try {
      if (!callbackUrl) {
        const kakaoAuthContext = sessionStorage.getItem('kakao_auth_context');

        const {
          callbackUrl: kakaoCallbackUrl = null,
          platform: kakaoPlatform = null,
        } = kakaoAuthContext ? JSON.parse(kakaoAuthContext) : {};

        if (kakaoCallbackUrl === null || kakaoPlatform === null) return;

        const updatedCallbackUrl = new URL(kakaoCallbackUrl);
        updatedCallbackUrl.searchParams.append('idToken', idToken);

        const isNeedOnboarding =
          !shouldSkipOnboarding(updatedCallbackUrl) &&
          (await getIsNeedOnboarding({
            uid,
            token: idToken,
          }));

        if (isNeedOnboarding) {
          updatedCallbackUrl.pathname = '/onboarding';
        }

        const redirectUrl = isLinkingSocialAccount
          ? `${PAGE_PATHS.CONNECT_DONE}?callbackUrl=${encodeURIComponent(
              updatedCallbackUrl.toString(),
            )}&linkedMethod=${linkedMethod}`
          : updatedCallbackUrl.toString();

        window.location.href = redirectUrl;
        return;
      }

      if (callbackUrl) {
        const isWebPlatform = platform === 'web';
        const tokenParam = isWebPlatform ? 'idToken' : 'token';
        const tokenValue = isWebPlatform ? idToken : customToken || '';

        const updatedCallbackUrl = new URL(callbackUrl);
        updatedCallbackUrl.searchParams.append(tokenParam, tokenValue);

        const isNeedOnboarding =
          !shouldSkipOnboarding(updatedCallbackUrl) &&
          (await getIsNeedOnboarding({
            uid,
            token: idToken,
          }));

        if (isNeedOnboarding) {
          updatedCallbackUrl.pathname = '/onboarding';
        }

        const redirectUrl = isLinkingSocialAccount
          ? `${PAGE_PATHS.CONNECT_DONE}?callbackUrl=${encodeURIComponent(
              updatedCallbackUrl.toString(),
            )}&linkedMethod=${linkedMethod}`
          : updatedCallbackUrl.toString();

        window.location.href = redirectUrl;
      }
    } catch (error) {
      captureException(error);
    }
  };

  const handleLogin = async () => {
    await handleFormValidation();

    if (doesFormHaveErrors() === true) {
      const inputEl = formValidationErrors.email.isValid ? passwordEl : emailEl;
      const errorEl = formValidationErrors.email.isValid
        ? passwordError
        : emailError;

      focusAndAnimateError(inputEl, errorEl);

      return;
    }

    try {
      globalSpinnerController.showSpinner();
      if (saveIdChk.checked) {
        localStorage.setItem('savedId', JSON.stringify(emailEl.value));
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        emailEl.value,
        passwordEl.value,
      );

      // 계정 연동 모드인 경우 socialSignInError로부터 credential 생성
      if (isLinkingSocialAccount) {
        try {
          await linkEmailAccount({
            emailLoginUserCredential: userCredential,
            from,
          });
          return;
        } catch (error) {
          globalSpinnerController.hideSpinner();
          captureException(error);
          showLinkAccountErrorDialog(
            error instanceof FirebaseError &&
              error.code === 'auth/credential-already-in-use'
              ? 'alreadyInUse'
              : 'generic',
          );
          return;
        }
      }

      const idToken = await userCredential.user.getIdToken(true);
      const { customToken } = await idTokenHandler({
        idToken,
        actionType: ACTION_TYPES.EMAIL_SIGNIN,
      });

      if (platform === 'androidApp') {
        moveToAndroidLoginSuccessPage({
          customToken,
          providerType: 'email',
        });
        return;
      }

      if (isAppleDeviceWebApp) {
        await postTokensToIOSApp({
          idToken,
          customToken,
        });
      }

      if (isCalledFromParentSite) {
        window.opener.postMessage(
          {
            result: 'success',
            data: customToken,
          },
          '*',
        );
      } else {
        redirectToUpdatedCallbackUrl({
          idToken,
          customToken,
          uid: userCredential.user.uid,
        });
      }

      // 로그인 성공 시 credential 및 error 삭제
      clearCredentialForLinkAccount();
      clearSocialSignInError();

      saveProviderType(userCredential.providerId);
    } catch (error: any) {
      if (error.code === 'auth/internal-error') {
        const isPermissionDenied = `${error.message}`.includes(
          'PERMISSION_DENIED',
        );

        if (isPermissionDenied) {
          blockUserDialog.showModal();
        }
      }

      //TODO: type 수정 필요

      captureException(error);
      localStorage.removeItem('savedId');
      if (error.code === 'auth/wrong-password') {
        const result = await fetchSignInMethodsForEmail(auth, emailEl.value);

        if (result.includes('password')) {
          passwordError.textContent = i18next.t('wrongPassword');
          applyBorderBottomErrorStyle(passwordEl);

          return;
        }
      }
    } finally {
      globalSpinnerController.hideSpinner();
    }
  };

  loginBtn.addEventListener('click', handleLogin);
  passwordEl.addEventListener('keyup', (event) => {
    if (event.code === Enter) {
      passwordEl.blur();
      handleLogin();
    }
  });
  emailEl.addEventListener('blur', handleEmailValidation);
  passwordEl.addEventListener('blur', handlePasswordValidation);
  pwVisible.addEventListener('click', pwVisibleClickHandler);
  checkBox.addEventListener('click', checkBoxClickHandler);
  saveIdChk.addEventListener('click', saveIdChkClickHandler);

  const displaySocialLoginContainer = async () => {
    PasswordLoginContainer.style.display = 'none';
    const SocialLoginContainer = document.getElementById(
      'social-login-container',
    );
    if (SocialLoginContainer) SocialLoginContainer.style.display = 'block';
  };

  const handlePopState = () => {
    const { display } = window.getComputedStyle(PasswordLoginContainer);
    const isPasswordLoginContainerDisplayed = display === 'block';

    if (isLinkingSocialAccount && isPasswordLoginContainerDisplayed) {
      displaySocialLoginContainer();
    }
  };

  window.addEventListener('popstate', handlePopState);
}
